import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, publishLast, refCount, switchMap, tap } from 'rxjs/operators';
import { IdentifierType, MemberIdentifier } from '../../../core/_models/member-interface';
import { CreateMemberResponse, MemberCreateData, MemberService } from '../../../core/members/member.service';
import { SettingsService } from '../../../core/settings/settings.service';
import { Msisdn } from '../../../shared/models/msisdn';
import { Angulartics2 } from 'angulartics2';
import { SessionService } from '../../../core/session/session.service';
import { Settings } from '../../../core/_models/settings';
import { CookieConsentService, COOKIES_CONSENT_KEY } from '@app/core/cookie-consent/cookie-consent.service';
import {
  MemberFormData,
  ProfileFormComponent,
} from '@app/+profile-form/components/profile-form/profile-form.component';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MsisdnRegisterOtpService } from '@app/core/recaptcha/msisdn-register-otp.service';
import { transformProfileErrors } from '@app/+profile-form/views/profile-errors-helpers';
import { RecaptchaData, TooLowRecaptchaScoreError } from '@app/core/recaptcha/recaptcha-commons';
import { NgClass } from '@angular/common';
import { MsisdnInputComponent } from '@app/shared/components/msisdn-input/msisdn-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';
import { PasswordResendCaptchaComponent } from '@app/shared/components/password-resend-captcha/password-resend-captcha.component';
import { RecaptchaComponent } from '@app/core/recaptcha/components/recaptcha/recaptcha.component';
import { LoyaltyClubHeaderComponent } from '@app/shared/components/loyalty-club-header/loyalty-club-header.component';
import { TopNavbarComponent } from '@app/shared/components/top-navbar/top-navbar.component';
import { LoyaltyClubFooterComponent } from '@app/shared/components/loyalty-club-footer/loyalty-club-footer.component';
import { AUTOLOGIN_ENCODED_MSISDN_QUERY_PARAM } from '@app/core/consts';

import { MemberValidationError } from '@app/core/members/interfaces';
import { RecaptchaInfoComponent } from '@app/core/recaptcha/components/recaptcha-info/recaptcha-info.component';

enum FlowStep {
  form = 'form',
  recaptchaCheckbox = 'recaptcha',
  otpInput = 'otp'
}

@Component({
  selector: 'app-new-profile',
  templateUrl: './new-profile.component.html',
  imports: [
    FormsModule,
    NgClass,
    MsisdnInputComponent,
    TranslatePipe,
    ReactiveFormsModule,
    InputErrorsComponent,
    PasswordResendCaptchaComponent,
    RecaptchaComponent,
    ProfileFormComponent,
    LoyaltyClubHeaderComponent,
    TopNavbarComponent,
    LoyaltyClubFooterComponent,
    RecaptchaInfoComponent,
  ],
})
export class NewProfileComponent implements OnDestroy {
  public readonly FLOW_STEPS = FlowStep;
  public prefilledData: any = { properties: {} };
  public isMemberChecked = false;
  public formErrors: any = {};
  public otpFormControl = new UntypedFormControl('', Validators.required);
  public msisdnControl = new UntypedFormControl('');
  public settingsObservable: Observable<Settings> = this.settingsService
    .getSettingsOnce().pipe(publishLast(), refCount());
  public isMsisdnFlow = false;
  public flowStep: FlowStep = FlowStep.form;
  public isPasswordSending = false;
  public isPasswordSent = false;
  public isSecondStepSubmitted = false;
  public showCaptchaCheckbox = false;

  private currentQueryParams: ParamMap;
  private subscription = new Subscription();

  private get encryptedReservedId(): string | null { return this.currentQueryParams.get('erid'); }

  constructor(private route: ActivatedRoute,
              private router: Router,
              private memberService: MemberService,
              private settingsService: SettingsService,
              private analytics: Angulartics2,
              private sessionService: SessionService,
              private cookieConsentService: CookieConsentService,
              private msisdnRegisterOtpService: MsisdnRegisterOtpService) {
    this.redirectToLoginIfMemberExists();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onProfileSave(form: MemberFormData): void {
    if (this.encryptedReservedId) { form.encrypted_reserved_id = this.encryptedReservedId; }
    this.formErrors = {};
    if (!this.isMsisdnFlow) {
      this.createMember(form);
      return;
    }
    this.handleMsisdnFirstStepSave(form);
  }

  public resendOTP(): void {
    this.isPasswordSent = false;
    this.isPasswordSending = true;
    const sub = this.sendBaseOtp().pipe(
      catchError(error => {
        if (error instanceof TooLowRecaptchaScoreError) {
          this.showCaptchaCheckbox = true;
        }
        throw error;
      }),
    ).subscribe(() => {
      this.isPasswordSending = false;
      this.isPasswordSent = true;
    });
    this.subscription.add(sub);
  }

  public submitSecondStep(): void {
    this.isSecondStepSubmitted = true;
    if (!this.otpFormControl.valid) {
      this.formErrors = {
        registration_password: { error: 'not_contain_required_property' },
      };
      return;
    }
    this.createMember({
      ...this.prefilledData,
      registration_password: this.otpFormControl.value,
    });
  }

  public recaptchaCheckboxDataGeneratedForOtpStep(data: RecaptchaData): void {
    const sub = this.sendCheckboxOtp(data)
      .pipe(
        tap(() => {
          this.showCaptchaCheckbox = false;
          this.isPasswordSending = false;
          this.isPasswordSent = true;
        }),
      ).subscribe();
    this.subscription.add(sub);
  }

  public recaptchaCheckboxDataGenerated(data: RecaptchaData): void {
    const sub = this.sendCheckboxOtp(data)
      .pipe(
        tap(() => this.changeStep(FlowStep.otpInput)),
      ).subscribe();
    this.subscription.add(sub);
  }

  private createMember(form: MemberCreateData): void {
    const sub = this.memberService.create(form).subscribe(
      (json) => this.onSuccessSave(json, form),
      (response) => this.onErrorSave(response?.error?.errors),
    );
    this.subscription.add(sub);
  }

  private handleMsisdnFirstStepSave(form: MemberCreateData): void {
    this.msisdnControl.setValue(form.msisdn);
    this.prefilledData = form;
    const { consents, toc, ...rest } = form;
    const sub = this.memberService.validate({ consents: consents ?? {}, properties: rest })
      .pipe(
        switchMap(() => this.initOtpInputStep()),
      ).subscribe({
        error: error => {
          if (error instanceof MemberValidationError) {
            this.onErrorSave(error.validationErrors);
            return;
          }
          throw error;
        },
      });
    this.subscription.add(sub);
  }

  private initOtpInputStep(): Observable<unknown> {
    return this.sendBaseOtp()
      .pipe(
        tap(() => this.changeStep(FlowStep.otpInput)),
        catchError(error => {
          if (error instanceof TooLowRecaptchaScoreError) {
            this.changeStep(FlowStep.recaptchaCheckbox);
            return of(null);
          }
          throw error;
        }),
      );
  }

  private sendBaseOtp(): Observable<unknown> {
    return this.msisdnRegisterOtpService.generateOTP(this.getIdentifier());
  }

  private sendCheckboxOtp(data: RecaptchaData): Observable<unknown> {
    return this.msisdnRegisterOtpService.generateOTPWithCheckboxData(
      this.getIdentifier(),
      data,
    );
  }

  private getIdentifier(): MemberIdentifier {
    return {
      identifier: this.msisdnControl.value,
      identifier_type: IdentifierType.MSISDN,
    };
  }

  private changeStep(step: FlowStep): void {
    this.flowStep = step;
  }

  private redirectToLoginIfMemberExists(): void {
    this.route.queryParamMap.pipe(
      tap((params) => this.currentQueryParams = params),
      switchMap(() => this.settingsObservable),
      switchMap((settings) => {
        const memberIdentifier = this.prepareMemberIdentifier(settings);
        return combineLatest(this.memberService.isMember(memberIdentifier), of(memberIdentifier));
      }),
    ).subscribe(([memberInfo, memberIdentifier]) => {
      if (memberInfo.exists) { return this.onMemberAlreadyExists(memberIdentifier); }

      this.parseQueryParams();
      // Display registration form if member is checked and does not exist.
      this.isMemberChecked = true;
      this.analytics.eventTrack.next({ action: 'member_registration_page' });
    });
  }

  private prepareMemberIdentifier(settings: Settings): MemberIdentifier {
    const identifierType: IdentifierType = <IdentifierType>settings.schema.identifiers?.[0];
    const memberIdentifier: MemberIdentifier = {
      identifier_type: identifierType,
      identifier: (this.currentQueryParams.get(identifierType) || this.currentQueryParams.get(identifierType[0])) as string,
    };

    if (identifierType === IdentifierType.MSISDN) {
      memberIdentifier.identifier = Msisdn.decodeMsisdn(memberIdentifier.identifier, true) as string;
    }

    return memberIdentifier;
  }

  private onMemberAlreadyExists(memberIdentifier: MemberIdentifier): void {
    const queryParams = {};
    queryParams[memberIdentifier.identifier_type] = this.currentQueryParams.get(memberIdentifier.identifier_type);
    this.router.navigate(['../../'],
      { relativeTo: this.route, replaceUrl: true, queryParams: queryParams });
  }

  private parseQueryParams(): void {
    const encodedMsisdn = this.currentQueryParams.get('msisdn') || this.currentQueryParams.get(AUTOLOGIN_ENCODED_MSISDN_QUERY_PARAM);
    const form = JSON.parse(this.currentQueryParams.get('form') as string);

    if (form) {
      this.prefilledData.properties = form;
    }
    if (!encodedMsisdn) {
      return;
    }
    this.isMsisdnFlow = true;
  }


  private onSuccessSave(json: CreateMemberResponse, form: any): void {
    this.cookieConsentService.toggle(form.consents[COOKIES_CONSENT_KEY], false);
    this.analytics.eventTrack.next({
      action: 'new',
      properties: { category: 'member_change', label: 'Succesfull registration' },
    });
    this.analytics.eventTrack.next({ action: 'become_member_page' });

    if (json.auth_token) { this.sessionService.saveAuthToken(json.auth_token); }


    if (this.currentQueryParams.has('target')) {
      this.onSuccessRedirectToTarget();
    } else if (json.auth_token) {
      this.onSuccessWithAutologin();
    } else {
      this.onSuccessRedirectToConfirmation(json, form);
    }
  }

  private onSuccessRedirectToTarget(): void {
    window.location.href = this.currentQueryParams.get('target') || '';
  }

  private onSuccessWithAutologin(): void {
    this.router.navigate(['../../member'],
      { relativeTo: this.route, replaceUrl: true, queryParams: { new: true } });
  }

  private onSuccessRedirectToConfirmation(json: CreateMemberResponse, form): void {
    const msisdn = json.properties ? json.properties.msisdn : form.msisdn;
    const email = json.properties ? json.properties.email : null;

    const navigationExtras: NavigationExtras = {
      queryParams: { msisdn: Msisdn.encodeMsisdn(msisdn), email: email },
      relativeTo: this.route,
      replaceUrl: true,
    };

    this.router.navigate(['..', 'signup_confirmation'], navigationExtras);
  }

  private onErrorSave(errors: any): void {
    this.formErrors = transformProfileErrors(errors);
  }
}
